import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearFilmStore,
  filmHref,
  filmOrigins,
  parseFilmSearch,
  readFilmStore,
  sameOriginHref,
  stripFilmSearch,
  thisFilmSite,
  writeFilmStore,
} from "./film.js";
import { FILM_LENGTH, SHOTS, shotIndexAt } from "./shots.js";
import FilmConsole from "./FilmConsole.jsx";

const FALLBACK = "main, .app-main, .site-main, .dashboard-frame, .landing-frame";
const WIPE_MS = 2800;

function headerFloor() {
  const header = document.querySelector(".site-header");
  return header ? header.getBoundingClientRect().bottom + 4 : 76;
}

function holeFrom(el, pad, camera, extra = {}) {
  if (!el) {
    return { top: 24, left: 24, width: window.innerWidth - 48, height: window.innerHeight - 96 };
  }
  const r = el.getBoundingClientRect();
  const maxRight = window.innerWidth - 10;
  const maxBottom = window.innerHeight - 16;
  const grow = camera === "zoom-out" ? 28 : 0;
  let width = r.width + pad * 2 + grow * 2;
  let height = extra.tight ? r.height + pad * 2 : r.height + pad * 2 + grow * 2;
  if (extra.minWidth) width = Math.max(width, extra.minWidth);
  let left = r.left - pad - grow;
  let top = r.top - pad - grow;
  if (left + width > maxRight) left = maxRight - width;
  left = Math.max(10, left);
  width = Math.min(maxRight - left, width);
  const minTop = el.closest?.(".journey-sidebar, .sidebar-frame") ? headerFloor() : 10;
  top = Math.max(minTop, top);
  height = Math.min(maxBottom - top, height);
  if (extra.clampEl) {
    const c = extra.clampEl.getBoundingClientRect();
    const bottom = Math.min(top + height, c.bottom);
    top = Math.max(top, c.top);
    height = Math.max(40, bottom - top);
  }
  return { top, left, width, height };
}

function wideHole() {
  const bar = document.documentElement.classList.contains("dh-film-record") ? 12 : 92;
  return { top: 8, left: 8, width: window.innerWidth - 16, height: window.innerHeight - bar };
}

function pickEl(shot) {
  try {
    return (
      (shot.selector ? document.querySelector(shot.selector) : null) ||
      (shot.fallback ? document.querySelector(shot.fallback) : null) ||
      document.querySelector(FALLBACK)
    );
  } catch {
    return document.querySelector(FALLBACK);
  }
}

let motionIds = [];
let rafIds = [];

function clearMotion() {
  motionIds.forEach((id) => window.clearTimeout(id));
  rafIds.forEach((id) => window.cancelAnimationFrame(id));
  motionIds = [];
  rafIds = [];
}

function later(fn, ms) {
  const id = window.setTimeout(fn, ms);
  motionIds.push(id);
  return id;
}

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - (2 - 2 * t) ** 2 / 2;
}

function resetToTop() {
  document.querySelectorAll(
    ".desk-frame__body, .sidebar-frame__body, .journey-sidebar__body, .journey-frame__body, .landing-frame, .dashboard-frame, .site-main"
  ).forEach((box) => {
    box.scrollTop = 0;
  });
  const root = document.scrollingElement || document.documentElement;
  if (root) root.scrollTop = 0;
  window.scrollTo(0, 0);
}

function animateScroll(box, top, duration) {
  if (!box) return;
  const start = box.scrollTop;
  const dist = top - start;
  const t0 = performance.now();
  function step(now) {
    const t = Math.min(1, (now - t0) / duration);
    box.scrollTop = start + dist * easeInOut(t);
    if (t < 1) rafIds.push(window.requestAnimationFrame(step));
  }
  rafIds.push(window.requestAnimationFrame(step));
}

function readZoom(key) {
  const stored = Number(sessionStorage.getItem(key));
  if (stored >= 0.5 && stored <= 1.3) return stored;
  const label = document.querySelector(".frame-zoom__value");
  const fromLabel = Number.parseInt(label?.textContent || "", 10);
  return Number.isFinite(fromLabel) ? fromLabel / 100 : 0.7;
}

function animateZoom(el, zoom, zoomKey, duration = 1800) {
  stripCssZoom();
  if (zoomKey) {
    const from = readZoom(zoomKey);
    if (Math.abs(from - zoom) < 0.02) {
      window.dispatchEvent(new CustomEvent("dh-film-zoom", { detail: { zoom, key: zoomKey } }));
      return;
    }
    const t0 = performance.now();
    function step(now) {
      const t = Math.min(1, (now - t0) / duration);
      const value = Math.round((from + (zoom - from) * easeInOut(t)) * 20) / 20;
      window.dispatchEvent(new CustomEvent("dh-film-zoom", { detail: { zoom: value, key: zoomKey } }));
      if (t < 1) rafIds.push(window.requestAnimationFrame(step));
    }
    rafIds.push(window.requestAnimationFrame(step));
    return;
  }
  const frame = el?.closest?.(".journey-sidebar, .sidebar-frame, .dashboard-frame, main, article") || el;
  if (!frame || !zoom) return;
  const from = Number(frame.style.zoom) || 1;
  const t0 = performance.now();
  frame.setAttribute("data-film-zoom", "1");
  function step(now) {
    const t = Math.min(1, (now - t0) / duration);
    frame.style.zoom = String(from + (zoom - from) * easeInOut(t));
    if (t < 1) rafIds.push(window.requestAnimationFrame(step));
  }
  rafIds.push(window.requestAnimationFrame(step));
}

function restoreZoom() {
  ["dh_sidebar_zoom_v3", "dh_desk_zoom_v2", "dh_frame_zoom_v2"].forEach((key) => {
    window.dispatchEvent(new CustomEvent("dh-film-zoom", { detail: { zoom: 0.7, key } }));
  });
  stripCssZoom();
}

function applyFilmZoom(el, zoom, zoomKey) {
  if (!zoom || !el) return;
  animateZoom(el, zoom, zoomKey);
}

function filmClick(el, shot) {
  const target = (shot.click ? document.querySelector(shot.click) : null) || el;
  const btn =
    target?.matches?.("button")
      ? target
      : target?.querySelector?.("button[aria-expanded], button[data-tour], button");
  if (!btn || btn.closest("a")) return;
  btn.click();
}

function scrollNodeInto(node, duration = 1100) {
  if (!node) return;
  const box = scrollerFor(node);
  const boxTop = box.getBoundingClientRect().top;
  const next = node.getBoundingClientRect().top - boxTop + box.scrollTop - Math.round((box.clientHeight || 360) * 0.18);
  const max = Math.max(0, box.scrollHeight - box.clientHeight);
  animateScroll(box, Math.max(0, Math.min(max, next)), duration);
}

function runAction(shot, el) {
  clearMotion();
  if (shot.zoom) applyFilmZoom(el, shot.zoom, shot.zoomKey);

  if (shot.action === "click") {
    later(() => filmClick(el, shot), 400);
  }
  if (shot.action === "top") {
    resetToTop();
  }
  if (shot.action === "scroll") {
    const box = scrollerFor(el);
    const downMs = shot.scanMs || 5000;
    box.scrollTo({ top: 0, behavior: "auto" });
    const down = Math.max(120, Math.min(box.scrollHeight - box.clientHeight, Math.round((box.clientHeight || 360) * 0.9)));
    later(() => animateScroll(box, down, downMs), 500);
  }
  if (shot.action === "scan") {
    const box = scrollerFor(el);
    const max = Math.max(0, box.scrollHeight - box.clientHeight);
    const downMs = shot.scanMs || 6500;
    box.scrollTo({ top: 0, behavior: "auto" });
    later(() => animateScroll(box, max, downMs), 500);
  }
}

function bubblePos(hole, line = "", opts = {}) {
  const nav = 80;
  const bar = 96;
  const long = String(line).length > 28;
  const w = opts.wide
    ? Math.min(360, Math.max(168, hole.width - 20))
    : Math.min(long ? 340 : 280, Math.max(118, 32 + String(line).length * (long ? 7 : 11)));
  const h = long ? 96 : 52;
  const pad = 16;
  let left = hole.left + hole.width - w - pad;
  let top = hole.top + hole.height - h - pad;
  if (opts.lift) top -= 38;
  if (top < nav) {
    top = hole.top + hole.height + pad;
    left = Math.min(Math.max(12, hole.left + hole.width - w), window.innerWidth - w - 12);
  }
  left = Math.min(Math.max(12, left), window.innerWidth - w - 12);
  top = Math.min(Math.max(nav, top), window.innerHeight - bar - h);
  return { top, left };
}

function storyPos() {
  return { top: Math.max(96, window.innerHeight * 0.15), left: 36 };
}

function dockPos() {
  const panel = document.querySelector("[data-tour='buyer-hero'], [data-tour='seller-hero']");
  const heading = panel?.querySelector("h1");
  const pr = panel?.getBoundingClientRect();
  const hr = heading?.getBoundingClientRect();
  if (!pr) return { top: 110, left: 28 };
  const width = 168;
  const left = hr ? Math.max(pr.left + 16, hr.left - width - 12) : pr.left + 16;
  return {
    top: Math.max(88, (hr?.top ?? pr.top) + 8),
    left,
  };
}

function centerTagPos() {
  const frame =
    document.querySelector(".desk-frame__body") ||
    document.querySelector("[data-tour='hub-hall']") ||
    document.querySelector("[data-tour='journey-home']") ||
    document.querySelector("main, .site-main, .app-main");
  const r = frame?.getBoundingClientRect();
  return {
    top: r ? r.top + r.height / 2 : window.innerHeight * 0.46,
    left: r ? r.left + r.width / 2 : window.innerWidth / 2,
  };
}

function isJourneyFilmPage() {
  return Boolean(document.querySelector(".app-shell.is-journey"));
}

function lockJourneyFilmViewport() {
  const root = document.scrollingElement || document.documentElement;
  root.scrollTop = 0;
  window.scrollTo(0, 0);
  document.body.scrollTop = 0;
  document.querySelectorAll(".journey-sidebar__body, .journey-frame__body").forEach((box) => {
    box.scrollTop = 0;
  });
}

function revealInJourneyFrame(el) {
  if (!el || el.closest?.(".journey-sidebar")) return;
  const frame = document.querySelector(".journey-frame__body");
  if (!frame) return;
  const frameTop = frame.getBoundingClientRect().top;
  const elTop = el.getBoundingClientRect().top;
  frame.scrollTop = Math.max(0, frame.scrollTop + (elTop - frameTop) - 20);
}

function scrollerFor(el) {
  const details = el?.matches?.(".event-details__scroll")
    ? el
    : el?.querySelector?.(".event-details__scroll") || el?.closest?.(".event-details")?.querySelector?.(".event-details__scroll");
  if (details && details.scrollHeight > details.clientHeight + 8) return details;
  const journeyRail = el?.closest?.(".journey-sidebar")?.querySelector?.(".journey-sidebar__body");
  if (journeyRail) return journeyRail;
  if (el && el.scrollHeight > el.clientHeight + 12) {
    const self = `${window.getComputedStyle(el).overflowY || ""} ${window.getComputedStyle(el).overflow || ""}`;
    if (/(auto|scroll)/.test(self)) return el;
  }
  const rail = el?.closest?.("aside")?.querySelector?.(".sidebar-frame__body");
  if (rail) return rail;
  const frame = el?.closest?.(".journey-frame__body, .desk-frame__body");
  if (frame && frame.scrollHeight > frame.clientHeight + 12) return frame;
  let node = el;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflow = `${style.overflowY || ""} ${style.overflow || ""}`;
    if (/(auto|scroll)/.test(overflow) && node.scrollHeight > node.clientHeight + 12) return node;
    node = node.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

function stripCssZoom() {
  document.querySelectorAll("[data-film-zoom]").forEach((node) => {
    node.style.zoom = "";
    node.removeAttribute("data-film-zoom");
  });
}

function clearFilmZoom() {
  stripCssZoom();
  window.dispatchEvent(new CustomEvent("dh-film-zoom", { detail: { zoom: 1 } }));
}

function elapsedNow(urlT0) {
  const store = readFilmStore() || {};
  if (store.paused && typeof store.elapsed === "number") return store.elapsed;
  const t0 = store.t0 || urlT0;
  if (!t0) return 0;
  return Math.max(0, (Date.now() - t0) / 1000);
}

export default function SalesTourHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const { on, shot: shotIndex, record, t0: urlT0 } = parseFilmSearch(location.search);
  const shot = SHOTS[Math.min(shotIndex, SHOTS.length - 1)];
  const [playing, setPlaying] = useState(() => {
    const store = readFilmStore() || {};
    const film = typeof window === "undefined" ? { on: false, t0: null } : parseFilmSearch(window.location.search);
    if (film.t0 && Number(store.t0) !== Number(film.t0)) return true;
    return Boolean(film.on && !store.paused);
  });
  const [clock, setClock] = useState(0);
  const [black, setBlack] = useState(() => {
    if (typeof window === "undefined") return false;
    const film = parseFilmSearch(window.location.search);
    if (film.fade) return true;
    if (!film.on) return false;
    const current = SHOTS[Math.min(film.shot, SHOTS.length - 1)];
    if (!current || current.kind === "card" || current.kind === "chaos" || current.camera === "full" || !current.camera) {
      return false;
    }
    return true;
  });
  const [hole, setHole] = useState(() =>
    typeof window === "undefined" ? { top: 12, left: 12, width: 300, height: 200 } : wideHole()
  );
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);
  const [fs, setFs] = useState(false);
  const [insetOn, setInsetOn] = useState(false);
  const [tagOn, setTagOn] = useState(false);
  const [titleCard, setTitleCard] = useState(() =>
    shot?.kind === "card" ? { title: shot.title, url: shot.url, sub: shot.sub } : null
  );
  const [cardOn, setCardOn] = useState(false);
  const [creditsOn, setCreditsOn] = useState(false);
  const [creditsHold, setCreditsHold] = useState(false);
  const site = thisFilmSite();
  const going = useRef(false);
  const goingAt = useRef(0);
  const pathRef = useRef("");
  const cardHeld = useRef(shot?.kind === "card");
  const cardOnRef = useRef(false);
  const holdTag = useRef(null);

  function showCard(next) {
    setTitleCard({ title: next.title, url: next.url, sub: next.sub });
    setBlack(true);
    if (cardOnRef.current) return;
    if (record) {
      cardOnRef.current = true;
      setCardOn(true);
      return;
    }
    setCardOn(false);
    window.setTimeout(() => {
      cardOnRef.current = true;
      setCardOn(true);
    }, 40);
  }

  const goShot = useCallback(
    (index) => {
      const next = SHOTS[index];
      if (!next || going.current) return;
      writeFilmStore({ playing: true, shot: index });
      const dest = sameOriginHref(next, index, record);
      const prev = SHOTS[shotIndex];
      const fromCard = prev?.kind === "card";
      const toCard = next.kind === "card";
      const pathChange = next.path !== location.pathname;
      const dissolve = next.site === site && pathChange && !fromCard && !toCard;
      if (next.site !== site) {
        going.current = true;
        goingAt.current = Date.now();
        document.documentElement.classList.add("dh-film");
        setBlack(true);
        const href = filmHref(next, index, record);
        const joiner = href.includes("?") ? "&" : "?";
        window.setTimeout(() => {
          window.location.assign(`${href}${joiner}fade=1`);
        }, WIPE_MS);
        return;
      }
      if (`${location.pathname}${location.search}` === dest) {
        going.current = false;
        return;
      }
      going.current = true;
      goingAt.current = Date.now();
      if (toCard) {
        setBlack(true);
        showCard(next);
      } else if (fromCard) {
        setBlack(true);
      }
      const move = () => {
        flushSync(() => {
          navigate(dest, { replace: next.path === location.pathname, preventScrollReset: true });
        });
      };
      const wait = toCard ? (record ? 160 : WIPE_MS) : fromCard ? 0 : dissolve ? 0 : 0;
      window.setTimeout(() => {
        if (dissolve && !record && typeof document.startViewTransition === "function") {
          document.startViewTransition(move).finished.finally(() => {
            going.current = false;
          });
        } else if (dissolve) {
          setBlack(true);
          window.setTimeout(() => {
            move();
            window.setTimeout(() => {
              setBlack(false);
              going.current = false;
            }, 40);
          }, WIPE_MS);
        } else {
          move();
          window.setTimeout(() => {
            going.current = false;
          }, 80);
        }
      }, wait);
    },
    [location.pathname, location.search, navigate, record, site, shotIndex]
  );

  const finish = useCallback(() => {
    clearFilmStore();
    setPlaying(false);
    if (site === "checklist") navigate("/", { replace: true });
    else window.location.assign(`${filmOrigins().checklist}/`);
  }, [navigate, site]);

  const exit = useCallback(() => {
    clearFilmStore();
    setPlaying(false);
    navigate(`${location.pathname}${stripFilmSearch(location.search)}` || "/", { replace: true });
  }, [location.pathname, location.search, navigate]);

  function pause() {
    writeFilmStore({ playing: false, paused: true, elapsed: elapsedNow(urlT0) });
    setPlaying(false);
  }

  function resume() {
    const elapsed = elapsedNow(urlT0);
    writeFilmStore({ playing: true, paused: false, t0: Date.now() - elapsed * 1000, elapsed });
    setPlaying(true);
  }

  function seekTo(seconds) {
    const next = Math.max(0, Math.min(FILM_LENGTH - 0.2, seconds));
    writeFilmStore({ playing: true, paused: false, t0: Date.now() - next * 1000, elapsed: next });
    setPlaying(true);
    going.current = false;
    const index = shotIndexAt(next);
    goShot(index);
  }

  useEffect(() => {
    if (!on || !shot) return undefined;
    const store = readFilmStore() || {};
    const fresh = urlT0 && Number(store.t0) !== Number(urlT0);
    const paused = fresh ? false : Boolean(store.paused);
    const t0 = urlT0 || store.t0 || Date.now();
    let elapsed = (Date.now() - t0) / 1000;
    let clockStart = t0;
    if (elapsed < -1 || (!urlT0 && elapsed >= FILM_LENGTH)) {
      clockStart = Date.now() - (shot.at || 0) * 1000;
      elapsed = (Date.now() - clockStart) / 1000;
    }
    writeFilmStore({
      playing: !paused,
      paused,
      shot: shotIndex,
      t0: clockStart,
      record,
    });
    if (!paused) setPlaying(true);
    else setPlaying(false);
    setClock(elapsed);
    going.current = false;

    const aim = (attempt = 0) => {
      if (isJourneyFilmPage()) lockJourneyFilmViewport();
      if (shot.kind === "chaos" || shot.kind === "card") {
        setHole(wideHole());
        if (shot.kind === "card") {
          setBlack(true);
        } else if (!parseFilmSearch(window.location.search).fade) {
          setBlack(false);
        }
        return;
      }
      const exact = shot.selector ? document.querySelector(shot.selector) : null;
      if (shot.selector && !exact && attempt < 6) {
        later(() => aim(attempt + 1), 180);
        return;
      }
      const el = pickEl(shot);
      const pathNow = `${shot.site}:${shot.path}`;
      if (pathRef.current && pathRef.current !== pathNow && !shot.zoom) restoreZoom();
      pathRef.current = pathNow;
      if (
        shot.action === "top" ||
        shot.camera === "full" ||
        shot.id === "agent-full" ||
        shot.id === "broker-full" ||
        String(shot.id || "").startsWith("journey-support")
      ) {
        if (shot.action === "top" || shot.id === "agent-full" || shot.id === "broker-full") restoreZoom();
        resetToTop();
      }
      const aside = el?.closest?.("aside");
      const journeyAside = el?.closest?.(".journey-sidebar");
      const tallPage = Boolean(el && el.getBoundingClientRect().height > window.innerHeight * 0.65);
      if (journeyAside) {
        if (shot.action !== "scroll" && shot.action !== "scan") {
          scrollNodeInto(el, 700);
        }
      } else if (aside) {
        scrollNodeInto(el, 700);
      } else if (shot.review) {
        const frame = document.querySelector(".desk-frame__body");
        if (frame) frame.scrollTop = 0;
      } else if (shot.action !== "scan" && shot.action !== "scroll" && shot.action !== "glide") {
        if (!tallPage && shot.camera !== "full") {
          if (isJourneyFilmPage()) {
            revealInJourneyFrame(el);
          } else {
            el?.scrollIntoView({
              block: shot.align || (String(shot.selector || "").includes("sidebar") ? "start" : "center"),
              inline: "nearest",
              behavior: "auto",
            });
          }
        }
      }
      runAction(shot, el);
      const pad = shot.tight ? 5 : shot.camera === "zoom-in" ? 10 : 18;
      const clampEl =
        shot.clamp === "landing"
          ? document.querySelector(".landing-frame")
          : shot.clamp === "desk-nav"
            ? document.querySelector("[data-tour='desk-nav']")
            : null;
      let nextHole =
        shot.camera === "full" || !shot.camera
          ? wideHole()
          : holeFrom(el, pad, shot.camera, { clampEl, tight: shot.tight });
      if (shot.wide && shot.camera === "zoom-in") {
        const pages = document.querySelector("[data-tour='footer-pages']");
        const minWidth = pages ? pages.getBoundingClientRect().width + pad * 2 : 0;
        nextHole = holeFrom(el, pad, shot.camera, { minWidth, clampEl, tight: shot.tight });
      }
      setHole(nextHole);
      if (shot.action === "glide" && Array.isArray(shot.glide)) {
        resetToTop();
        const nextAt = SHOTS[shotIndex + 1]?.at ?? shot.at + 12;
        const holdMs = Math.max(4000, (nextAt - shot.at) * 1000);
        const fadeOutAt = Math.max(2500, holdMs - 1600);
        const step = Math.max(1100, (fadeOutAt - 400) / Math.max(1, shot.glide.length));
        shot.glide.forEach((sel, i) => {
          later(() => {
            const node = document.querySelector(sel);
            if (!node) return;
            scrollNodeInto(node, Math.min(1400, step - 80));
            const expand = i >= (shot.expandAt ?? shot.glide.length - 1);
            setHole(
              holeFrom(node, expand ? 20 : 8, "zoom-in", expand ? { minWidth: Math.min(window.innerWidth * 0.74, node.getBoundingClientRect().width + 72) } : {})
            );
          }, 280 + i * step);
        });
        later(() => setHole(wideHole()), fadeOutAt);
      }
      later(() => setBlack(false), record ? 240 : 80);
    };
    const aimId = window.setTimeout(() => aim(0), 80);
    const onResize = () => setHole(shot.camera === "full" || !shot.camera ? wideHole() : holeFrom(pickEl(shot), 16, shot.camera));
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(aimId);
      window.removeEventListener("resize", onResize);
    };
  }, [on, record, shot, shotIndex, urlT0]);

  useEffect(() => {
    if (!on) return undefined;
    document.documentElement.classList.add("dh-film");
    if (record) document.documentElement.classList.add("dh-film-record");
    else document.documentElement.classList.remove("dh-film-record");
    return () => {
      document.documentElement.classList.remove("dh-film");
      document.documentElement.classList.remove("dh-film-record");
      clearMotion();
      clearFilmZoom();
    };
  }, [on, record]);

  useEffect(() => {
    if (!shot?.still && shot?.overlay !== "triptych" && shot?.overlay !== "support-close") {
      setInsetOn(false);
      return undefined;
    }
    setInsetOn(false);
    const nextAt = SHOTS[shotIndex + 1]?.at ?? shot.at + 7;
    const holdMs = Math.max(2000, (nextAt - shot.at) * 1000);
    const fadeAt = shot.stillLinger ? holdMs : Math.max(1800, holdMs - 1100);
    const inId = window.setTimeout(() => setInsetOn(true), 20);
    const outId = window.setTimeout(() => setInsetOn(false), 20 + fadeAt);
    return () => {
      window.clearTimeout(inId);
      window.clearTimeout(outId);
    };
  }, [shot?.id, shot?.still, shot?.stillLinger, shot?.overlay, shot?.at, shotIndex]);

  useEffect(() => {
    const line =
      shot?.line && shot.kind !== "card" && shot.kind !== "chaos" && shot.overlay !== "triptych";
    if (!line) {
      setTagOn(false);
      return undefined;
    }
    setTagOn(false);
    const nextAt = SHOTS[shotIndex + 1]?.at ?? shot.at + 3;
    const holdMs = Math.max(500, (nextAt - shot.at) * 1000);
    const fadeAt = Math.min(3000, holdMs - 400);
    const inId = window.setTimeout(() => setTagOn(true), 40);
    const outId = window.setTimeout(() => setTagOn(false), fadeAt);
    return () => {
      window.clearTimeout(inId);
      window.clearTimeout(outId);
    };
  }, [shot?.id, shot?.line, shot?.kind, shot?.overlay, shot?.at, shotIndex]);

  useEffect(() => {
    if (shot?.kind === "card") {
      cardHeld.current = true;
      setTitleCard({ title: shot.title, url: shot.url, sub: shot.sub });
      setBlack(true);
      if (cardOnRef.current) return undefined;
      if (record) {
        cardOnRef.current = true;
        setCardOn(true);
        return undefined;
      }
      setCardOn(false);
      const inId = window.setTimeout(() => {
        cardOnRef.current = true;
        setCardOn(true);
      }, 40);
      return () => window.clearTimeout(inId);
    }
    if (!cardHeld.current) {
      cardOnRef.current = false;
      setCardOn(false);
      setTitleCard(null);
      return undefined;
    }
    cardHeld.current = false;
    cardOnRef.current = false;
    setCardOn(false);
    const outId = window.setTimeout(() => setTitleCard(null), WIPE_MS);
    return () => window.clearTimeout(outId);
  }, [shot?.id, shot?.kind, shot?.title, shot?.url, shot?.sub]);

  useEffect(() => {
    if (shot?.credits) {
      setCreditsHold(true);
      setCreditsOn(false);
      const inId = window.setTimeout(() => setCreditsOn(true), 40);
      return () => window.clearTimeout(inId);
    }
    setCreditsOn(false);
    const outId = window.setTimeout(() => setCreditsHold(false), WIPE_MS);
    return () => window.clearTimeout(outId);
  }, [shot?.credits]);

  useEffect(() => {
    if (!on) return undefined;
    const film = parseFilmSearch(location.search);
    if (!film.fade) return undefined;
    const delay = shot?.kind === "card" ? WIPE_MS : 40;
    const outId = window.setTimeout(() => setBlack(false), delay);
    return () => window.clearTimeout(outId);
  }, [on, location.search, shot?.kind]);

  useEffect(() => {
    if (!on) return undefined;
    const tick = window.setInterval(() => {
      if (going.current && Date.now() - goingAt.current > 12000) going.current = false;
      const elapsed = elapsedNow(urlT0);
      setClock(elapsed);
      if (readFilmStore()?.paused || readFilmStore()?.playing === false) return;
      if (elapsed >= FILM_LENGTH) {
        finish();
        return;
      }
      const want = shotIndexAt(elapsed);
      if (want !== shotIndex) {
        const next = SHOTS[want];
        goShot(want);
      }
    }, 200);
    return () => window.clearInterval(tick);
  }, [finish, goShot, location.pathname, on, shotIndex, site, urlT0]);

  function toggleFs() {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.();
  }

  useEffect(() => {
    function onFs() {
      setFs(Boolean(document.fullscreenElement));
    }
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => {
    if (!on) return undefined;
    function onKey(event) {
      if (event.key === "Escape") exit();
      if (event.key === " " || event.code === "Space") {
        event.preventDefault();
        if (readFilmStore()?.paused) resume();
        else pause();
      }
      if (event.key === "f" || event.key === "F") toggleFs();
      if (event.key === "m" || event.key === "M") setMuted((value) => !value);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [exit, on]);

  if (!on || !shot || !hole) return null;

  const line =
    shot.kind === "card" || shot.kind === "chaos" || shot.overlay === "triptych" || shot.credits
      ? ""
      : shot.still && !shot.line
        ? ""
        : shot.line || "";
  const quiet = shot.kind === "chaos" || shot.kind === "card";
  const showCut = !quiet && (shot.camera === "zoom-in" || shot.camera === "zoom-out");
  const longLine = String(line).length > 28;
  let tag = null;
  if (line) {
    if (shot.tag === "center") tag = centerTagPos();
    else if (shot.tag === "story") tag = storyPos();
    else if (shot.tag === "dock") tag = dockPos();
    else if (shot.tag === "hold") {
      if (!holdTag.current) holdTag.current = bubblePos(hole, line, { lift: shot.tagLift, wide: shot.wide });
      tag = holdTag.current;
    } else {
      holdTag.current = null;
      tag = bubblePos(hole, line, { lift: shot.tagLift, wide: shot.wide });
    }
  } else if (shot.tag !== "hold") {
    holdTag.current = null;
  }

  return (
    <div className={`sales-tour${record ? " is-record" : ""}`} aria-live="polite">
      {record || quiet ? null : (
        <button
          type="button"
          className="sales-tour__hit"
          aria-label={playing ? "Pause" : "Play"}
          onClick={() => (playing ? pause() : resume())}
        />
      )}
      {quiet ? null : (
        <>
          <div
            className={`sales-tour__cut${showCut ? "" : " is-off"}`}
            style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
          />
          {line && tag ? (
            <div
              className={`sales-tour__bubble${tagOn ? " is-in" : ""}${shot.tag === "center" ? " is-center" : ""}${
                shot.tag === "center" ? (shot.tagSlide === "up" ? " is-from-up" : " is-from-down") : ""
              }${shot.tagTone === "pale" ? " is-pale" : ""}${shot.tag === "hold" ? " is-hold" : ""}${
                shot.tag === "dock" ? " is-dock" : ""
              }${longLine || shot.tag === "story" ? " is-long" : ""}`}
              style={{ top: tag.top, left: tag.left }}
            >
              <p>{line}</p>
            </div>
          ) : null}
        </>
      )}
      {shot.overlay === "triptych" || shot.overlay === "support-close" ? (
        <div className={`sales-tour__triptych is-inset${insetOn ? " is-in" : ""}`}>
          <span>Confidence.</span>
          <span>Clarity.</span>
          <span>Momentum.</span>
        </div>
      ) : null}
      {shot.overlay === "index-list" || shot.overlay === "support-close" ? (
        <ol className="sales-tour__index">
          <li>User manuals</li>
          <li>Guided Tour</li>
          <li>FAQ</li>
          <li>Contact</li>
          <li>Vendors</li>
          <li>Events and calendar</li>
          <li>Community</li>
          <li>Billing</li>
        </ol>
      ) : null}
      {titleCard ? (
        <button
          type="button"
          className={`sales-tour__titlecard${cardOn ? " is-in" : ""}`}
          aria-label={playing ? "Pause" : "Play"}
          onClick={() => (playing ? pause() : resume())}
        >
          <div className="sales-tour__titlecard-block">
            <strong>{titleCard.title}</strong>
            {titleCard.sub ? <em>{titleCard.sub}</em> : null}
            <span>{titleCard.url}</span>
          </div>
        </button>
      ) : null}
      {creditsHold ? (
        <div className={`sales-tour__credits${creditsOn ? " is-in" : ""}`}>
          <div className="sales-tour__credits-card">
            <p>Built for Real Estate Professionals</p>
            <p>Built for the Dream Home Journey</p>
            <strong>Built for You.</strong>
          </div>
        </div>
      ) : null}
      <div className={`sales-tour__black${black ? " is-on" : ""}`} />
      {shot.still ? (
        <div className={`sales-tour__inset${insetOn ? " is-in" : ""}`}>
          <img src={shot.still} alt="" />
          {shot.stillLine ? <p>{shot.stillLine}</p> : null}
        </div>
      ) : null}
      {record ? null : (
        <FilmConsole
          clock={Math.min(clock, FILM_LENGTH)}
          duration={FILM_LENGTH}
          playing={playing}
          record={record}
          onToggle={() => (playing ? pause() : resume())}
          onStop={exit}
          onSeek={seekTo}
          onSkip={(delta) => seekTo(elapsedNow(urlT0) + delta)}
          volume={muted ? 0 : volume}
          onVolume={(next) => {
            setVolume(next);
            setMuted(next === 0);
          }}
          muted={muted}
          onMute={() => setMuted((value) => !value)}
          onFs={toggleFs}
          fs={fs}
        />
      )}
    </div>
  );
}
